/**
 * Tutor API client - Can Do, sessions, progress.
 * Uses gateway (or VITE_AI_TUTOR_BASE_URL when set) for auth forwarding.
 */
import { isAxiosError } from 'axios';
import { createHttpClient } from '../../../api/http';
import { normalizeTutorBaseUrl } from '../../../api/tutorHttpBases';
import { serviceUrls } from '../../../config/serviceUrls';

const TUTOR_BASE = import.meta.env.VITE_AI_TUTOR_BASE_URL || serviceUrls.gateway;
const client = createHttpClient(TUTOR_BASE);

/**
 * Gateway BFF mounts tutor routes as `/v1/tutor/*`.
 * The direct ai-tutor FastAPI app mounts them as `/tutor/*` (no `/v1`).
 * Override with `VITE_TUTOR_REST_API_PREFIX=/tutor` or `/v1/tutor` when auto-detection is wrong.
 */
function tutorRestApiPrefix(baseUrl: string): string {
  const explicit = import.meta.env.VITE_TUTOR_REST_API_PREFIX?.trim();
  if (explicit === '/tutor' || explicit === '/v1/tutor') return explicit;

  const b = normalizeTutorBaseUrl(baseUrl).toLowerCase();
  if (b.includes('ai-tutor')) return '/tutor';
  // Default local direct tutor port (see `tutorHttpBases` FALLBACK_TUTOR)
  if (/:8007(\/|$)/.test(b)) return '/tutor';
  return '/v1/tutor';
}

const PREFIX = tutorRestApiPrefix(TUTOR_BASE);

/** Textbook page from tutor API; `image_url` may be site-root path or absolute HTTPS (e.g. GCS). */
export interface TutorPageAsset {
  page_no: number;
  image_url: string;
  gcs_path?: string;
  type?: string;
}

export interface TutorSublessonItem {
  sublesson_no: string;
  sublesson_index: number;
  title: string;
  type?: string;
  page_start?: number;
  page_end?: number;
  /** Populated when the backend attaches textbook pages (grades 2–3). */
  page_assets?: TutorPageAsset[];
}

export interface LessonItem {
  lesson_no: number;
  lesson_title: string;
  lesson_type?: string;
  /** RAG `book_id` when present — used to scope Story Studio to the same textbook as the tutor. */
  book_id?: string | null;
  page_start?: number;
  page_end?: number;
  sublesson_count?: number;
  sublessons?: TutorSublessonItem[];
  /** Lesson-wide textbook pages (grades 2–3); sublesson may override with its own `page_assets`. */
  page_assets?: TutorPageAsset[];
  section_key?: string | null;
  topic_ta?: string | null;
}

/** AI tutor Mongo-backed chunk progress (per lesson) */
export interface TutorSublessonProgressDTO {
  sublesson_no: string;
  title?: string;
  type?: string;
  chunk_total: number;
  chunks_completed: number;
  percent: number;
}

export interface TutorLessonProgressDTO {
  ok: boolean;
  grade: number;
  lesson_no: number;
  lesson_percent: number;
  sublessons: TutorSublessonProgressDTO[];
  aggregation?: string;
}

export interface TutorLessonResumeDTO {
  ok: boolean;
  grade: number;
  lesson_no: number;
  sublesson_no: string;
  chunk_order_index: number;
  last_position?: unknown;
  from_saved_session?: boolean;
  /** Resumed sublesson textbook pages when provided (grades 2–3). */
  page_assets?: TutorPageAsset[];
}

export interface CanDoItem {
  id: string;
  statement_ta: string;
  statement_en: string;
  rubric_levels?: { level: number; label: string; criteria: string }[];
  /** Set for Communication skills (lesson slot 0). */
  communication_topic_id?: string;
}

export interface CommunicationTopicMeta {
  id: string;
  title_en: string;
  title_ta?: string;
}

export interface CanDoCreatePayload {
  statement_ta: string;
  statement_en: string;
  rubric_levels?: { level: number; label: string; criteria: string }[];
  communication_topic_id?: string;
}

export const listLessons = async (): Promise<LessonItem[]> => {
  const r = await client.get<{ ok: boolean; lessons: LessonItem[] }>(`${PREFIX}/lesson-index`);
  return r.data?.lessons ?? [];
};

/** Teacher-only: lessons for a specific grade (roster student /lesson-index is not usable for teachers). */
export const listTeacherLessons = async (grade: number): Promise<LessonItem[]> => {
  const r = await client.get<{ ok: boolean; lessons: LessonItem[] }>(`${PREFIX}/teacher/lesson-index`, {
    params: { grade },
  });
  return r.data?.lessons ?? [];
};

export interface StoryItem {
  lesson_no: number;
  lesson_title: string;
  video_url: string;
  thumbnail_url: string | null;
}

/** Response from `GET /v1/tutor/communication-instruction` (topic + dialect resolved prompt). */
export interface CommunicationInstructionResponse {
  ok?: boolean;
  lesson_title?: string;
  lesson_style?: string;
  lesson_type?: string;
  dialect_key?: string;
  systemInstruction?: string;
  topic_id?: string;
  topic_title?: string;
  error?: string;
  message?: string;
}

/**
 * Loads the Communication Tutor system instruction.
 * Primary path: `topic_id` + `dialect_key`. Optional `topic_label` for legacy backends only.
 */
export async function getCommunicationInstruction(
  params: {
    topicId?: string;
    dialectKey: string;
    /** Legacy fallback when `topic_id` is unavailable */
    topicLabel?: string;
  },
  opts?: { signal?: AbortSignal }
): Promise<CommunicationInstructionResponse> {
  const dialectKey = String(params.dialectKey || '').trim();
  if (!dialectKey) throw new Error('dialect_key is required.');

  const tid = String(params.topicId || '').trim();
  const label = String(params.topicLabel || '').trim();

  const query: Record<string, string> = { dialect_key: dialectKey };
  if (tid) {
    query.topic_id = tid;
  } else if (label) {
    query.topic_label = label;
  } else {
    throw new Error('Missing topic: provide topic_id or topic_label.');
  }

  try {
    const r = await client.get<CommunicationInstructionResponse>(
      `${PREFIX}/communication-instruction`,
      { params: query, signal: opts?.signal }
    );
    const data = r.data ?? {};
    if (data.ok === false) {
      const msg =
        (typeof data.error === 'string' && data.error.trim()) ||
        (typeof data.message === 'string' && data.message.trim()) ||
        'Communication instruction request was not successful.';
      throw new Error(msg);
    }
    return data;
  } catch (e: unknown) {
    if (isAxiosError(e)) {
      const status = e.response?.status;
      const data = e.response?.data;
      const bodyText =
        typeof data === 'string' ? data : JSON.stringify(data ?? {});
      if (status === 404) {
        throw new Error(`Topic not found (404). ${bodyText}`.trim());
      }
      if (status === 502) {
        throw new Error(
          `Topic service unavailable (502). Please try again later. ${bodyText}`.trim()
        );
      }
      throw new Error(`Communication instruction failed: ${status ?? 'network'} ${bodyText}`.trim());
    }
    throw e;
  }
}

export const listStories = async (grade: number = 2): Promise<StoryItem[]> => {
  const r = await client.get<{ ok: boolean; grade: number; stories: StoryItem[] }>(
    `${PREFIX}/stories`,
    { params: { grade } }
  );
  return r.data?.stories ?? [];
};

export const listCanDos = async (grade: number, lessonNo: number): Promise<CanDoItem[]> => {
  const r = await client.get<{ ok: boolean; can_dos: CanDoItem[] }>(
    `${PREFIX}/lessons/${grade}/${lessonNo}/can-dos`
  );
  return r.data?.can_dos ?? [];
};

/** Lesson 0 = Communication Tutor skills (topics + can-dos). */
export const fetchCanDosBundle = async (
  grade: number,
  lessonNo: number
): Promise<{ can_dos: CanDoItem[]; communication_topics: CommunicationTopicMeta[] }> => {
  const r = await client.get<{
    ok: boolean;
    can_dos: CanDoItem[];
    communication_topics?: CommunicationTopicMeta[];
  }>(`${PREFIX}/lessons/${grade}/${lessonNo}/can-dos`);
  return {
    can_dos: r.data?.can_dos ?? [],
    communication_topics: r.data?.communication_topics ?? [],
  };
};

export const createCommunicationTopic = async (
  grade: number,
  payload: { title_en: string; title_ta?: string }
): Promise<CommunicationTopicMeta> => {
  const r = await client.post<{ ok: boolean; topic: CommunicationTopicMeta }>(
    `${PREFIX}/lessons/${grade}/0/communication-topics`,
    payload
  );
  return r.data.topic;
};

export const updateCommunicationTopic = async (
  grade: number,
  topicId: string,
  payload: { title_en?: string; title_ta?: string }
): Promise<CommunicationTopicMeta> => {
  const r = await client.put<{ ok: boolean; topic: CommunicationTopicMeta }>(
    `${PREFIX}/lessons/${grade}/0/communication-topics/${encodeURIComponent(topicId)}`,
    payload
  );
  return r.data.topic;
};

export const deleteCommunicationTopic = async (grade: number, topicId: string): Promise<void> => {
  await client.delete(
    `${PREFIX}/lessons/${grade}/0/communication-topics/${encodeURIComponent(topicId)}`
  );
};

export const createCanDo = async (
  grade: number,
  lessonNo: number,
  payload: CanDoCreatePayload
): Promise<CanDoItem> => {
  const r = await client.post<{ ok: boolean; can_do: CanDoItem }>(
    `${PREFIX}/lessons/${grade}/${lessonNo}/can-dos`,
    payload
  );
  return r.data.can_do;
};

export const updateCanDo = async (
  grade: number,
  lessonNo: number,
  canDoId: string,
  payload: Partial<CanDoCreatePayload>
): Promise<CanDoItem> => {
  const r = await client.put<{ ok: boolean; can_do: CanDoItem }>(
    `${PREFIX}/lessons/${grade}/${lessonNo}/can-dos/${canDoId}`,
    payload
  );
  return r.data.can_do;
};

export const deleteCanDo = async (
  grade: number,
  lessonNo: number,
  canDoId: string
): Promise<void> => {
  await client.delete(`${PREFIX}/lessons/${grade}/${lessonNo}/can-dos/${canDoId}`);
};

export const generateCanDos = async (
  grade: number,
  lessonNo: number
): Promise<CanDoItem[]> => {
  const r = await client.post<{ ok: boolean; can_dos: CanDoItem[] }>(
    `${PREFIX}/lessons/${grade}/${lessonNo}/can-dos/generate`
  );
  return r.data?.can_dos ?? [];
};

// --- Student Progress (Grade 2 students) ---

export interface LessonProgressItem {
  lesson_no: number;
  last_session_at: string | null;
  last_checkpoint: string | null;
  overall_score: number;
  can_do_scores: { can_do_id: string; score: number; assessed_at: string }[];
  sessions_count: number;
}

export interface StudentContext {
  last_lesson_no: number | null;
  last_lesson_title: string | null;
  last_session_at: string | null;
  current_lesson_no: number;
  last_checkpoint: string | null;
  summary?: string;
  next_steps?: string[];
}

export const getStudentContext = async (lessonNo: number): Promise<StudentContext> => {
  const r = await client.get<{
    ok: boolean;
    last_lesson_no: number | null;
    last_lesson_title: string | null;
    last_session_at: string | null;
    current_lesson_no: number;
    last_checkpoint: string | null;
    summary?: string;
    next_steps?: string[];
  }>(`${PREFIX}/students/me/context`, { params: { lesson_no: lessonNo } });
  return {
    last_lesson_no: r.data?.last_lesson_no ?? null,
    last_lesson_title: r.data?.last_lesson_title ?? null,
    last_session_at: r.data?.last_session_at ?? null,
    current_lesson_no: r.data?.current_lesson_no ?? lessonNo,
    last_checkpoint: r.data?.last_checkpoint ?? null,
    summary: r.data?.summary,
    next_steps: r.data?.next_steps,
  };
};

export const resetProgress = async (lessonNo: number): Promise<void> => {
  await client.post<{ ok: boolean; lesson_no: number }>(
    `${PREFIX}/students/me/progress/reset`,
    { lesson_no: lessonNo }
  );
};

export const getStudentProgress = async (): Promise<LessonProgressItem[]> => {
  const r = await client.get<{ ok: boolean; progress: LessonProgressItem[] }>(
    `${PREFIX}/students/me/progress`
  );
  return r.data?.progress ?? [];
};

// --- Teacher: student progress and session history ---

export interface TutorSessionItem {
  session_id: string;
  lesson_no: number;
  started_at: string | null;
  ended_at: string | null;
  duration_seconds: number;
  checkpoint_at_end: string | null;
  rubric_scores: Record<string, number>;
  status: string;
}

export const getTeacherStudentProgress = async (
  studentId: string
): Promise<LessonProgressItem[]> => {
  const r = await client.get<{ ok: boolean; progress: LessonProgressItem[] }>(
    `${PREFIX}/teacher/students/${studentId}/progress`
  );
  return r.data?.progress ?? [];
};

export const getTeacherStudentSessions = async (
  studentId: string,
  limit?: number
): Promise<TutorSessionItem[]> => {
  const r = await client.get<{ ok: boolean; sessions: TutorSessionItem[] }>(
    `${PREFIX}/teacher/students/${studentId}/sessions`,
    { params: limit != null ? { limit } : undefined }
  );
  return r.data?.sessions ?? [];
};

// --- Lesson chunk progress + resume (Mongo) ---

export const getTutorLessonProgress = async (
  lessonNo: number,
  grade?: number
): Promise<TutorLessonProgressDTO> => {
  const r = await client.get<TutorLessonProgressDTO>(`${PREFIX}/progress/lesson/${lessonNo}`, {
    params: grade != null ? { grade } : undefined,
  });
  return r.data;
};

/** One request for many lessons (dashboard); caps at 40 lesson numbers server-side. */
export const postTutorProgressBatch = async (
  lessonNos: number[],
  grade?: number
): Promise<TutorLessonProgressDTO[]> => {
  if (!lessonNos?.length) return [];
  const r = await client.post<{ ok: boolean; lessons: TutorLessonProgressDTO[] }>(
    `${PREFIX}/progress/batch`,
    { lesson_nos: lessonNos },
    { params: grade != null ? { grade } : undefined }
  );
  return r.data?.lessons ?? [];
};

export const getTutorLessonResume = async (
  lessonNo: number,
  grade?: number
): Promise<TutorLessonResumeDTO> => {
  const r = await client.get<TutorLessonResumeDTO>(`${PREFIX}/resume/${lessonNo}`, {
    params: grade != null ? { grade } : undefined,
  });
  return r.data;
};

export const postTutorProgressUpdate = async (
  payload: {
    lesson_no: number;
    sublesson_no: string;
    chunk_order_index: number;
    interaction_done?: boolean;
    time_spent_sec?: number;
  },
  grade?: number
): Promise<{ ok: boolean; status: string; time_spent_sec: number; interaction_done: boolean }> => {
  const r = await client.post(`${PREFIX}/progress/update`, payload, {
    params: grade != null ? { grade } : undefined,
  });
  return r.data;
};

export const postTutorSessionPosition = async (
  payload: {
    lesson_no: number;
    sublesson_no: string;
    chunk_order_index: number;
    last_position?: unknown;
  },
  grade?: number
): Promise<void> => {
  try {
    await client.post(`${PREFIX}/session/update`, payload, {
      params: grade != null ? { grade } : undefined,
    });
  } catch (e) {
    const detail = isAxiosError(e)
      ? `${e.response?.status} ${JSON.stringify(e.response?.data ?? e.message)}`
      : String(e);
    console.warn(
      "[TKM tutor] POST session/update failed (does not affect Live WebSocket; check chunk_order_index / server rules):",
      detail,
      { payload, grade }
    );
    throw e;
  }
};
